/**
 * SQLite Database Wrapper
 * Handles in-browser SQLite via sql.js with IndexedDB persistence
 */

import type { Database as SqlJsDatabase, SqlJsStatic } from 'sql.js';

// ============================================================================
// Database State
// ============================================================================

let db: SqlJsDatabase | null = null;
let SQL: SqlJsStatic | null = null;
let initialized = false;

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize SQLite database
 * Loads from IndexedDB if exists, creates new otherwise
 */
export async function initDB(): Promise<void> {
  if (initialized && db) {
    console.log('Database already initialized');
    return;
  }

  try {
    console.log('Loading SQL.js WASM...');
    // Load SQL.js WASM - dynamic import to handle ESM/CommonJS properly
    const sqlJsModule = await import('sql.js');
    console.log('sql.js module loaded:', sqlJsModule);
    console.log('Module keys:', Object.keys(sqlJsModule));
    console.log('Module.default:', sqlJsModule.default);
    console.log('typeof Module.default:', typeof sqlJsModule.default);

    // The actual function should be at sqlJsModule.default
    const initSqlJs = sqlJsModule.default;

    if (typeof initSqlJs !== 'function') {
      throw new Error(`Expected initSqlJs to be a function, got ${typeof initSqlJs}. Keys: ${Object.keys(sqlJsModule).join(', ')}`);
    }

    SQL = await initSqlJs({
      locateFile: (_file) => `/sql-wasm.wasm`
    });

    console.log('SQL.js loaded, restoring from IndexedDB...');
    // Try to restore from IndexedDB
    const savedData = await loadFromIndexedDB();

    if (savedData) {
      db = new SQL.Database(savedData);
      console.log('Database restored from IndexedDB');
    } else {
      db = new SQL.Database();
      console.log('New database created');
    }

    initialized = true;

    // Run migrations (will be imported in next step to avoid circular dependency)
    // Migrations will be run after DB initialization
  } catch (error) {
    console.error('Failed to initialize database:', error);
    throw new Error(`Database initialization failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}


/**
 * Get database instance
 */
export function getDB(): SqlJsDatabase {
  if (!db) throw new Error('Database not initialized. Call initDB() first.');
  return db;
}

/**
 * Check if database is initialized
 */
export function isDBInitialized(): boolean {
  return initialized && db !== null;
}

// ============================================================================
// Query Helpers
// ============================================================================

/**
 * Execute a query and return results as objects
 */
export function query<T = any>(sql: string, params: any[] = []): T[] {
  const db = getDB();

  // Convert parameters to SQL-compatible types (same as execute())
  const sqlParams = params.map(param => {
    if (param === undefined) return null;
    if (param instanceof Date) return param.toISOString();
    return param;
  });

  // Use prepared statement for parameter binding
  const stmt = db.prepare(sql);
  stmt.bind(sqlParams);

  const results: T[] = [];
  const columns: string[] = stmt.getColumnNames();

  while (stmt.step()) {
    const row = stmt.get();
    const obj: any = {};
    columns.forEach((col, i) => {
      obj[col] = row[i];
    });
    results.push(obj as T);
  }

  stmt.free();
  return results;
}

/**
 * Execute a query and return first result
 */
export function queryOne<T = any>(sql: string, params: any[] = []): T | null {
  const results = query<T>(sql, params);
  return results.length > 0 ? results[0] : null;
}

/**
 * Execute a non-query command (INSERT, UPDATE, DELETE)
 */
export function execute(sql: string, params: any[] = []): void {
  const db = getDB();

  // Convert parameters to SQL-compatible types
  const sqlParams = params.map(param => {
    // Convert undefined to null
    if (param === undefined) return null;

    // Convert Date objects to ISO strings
    if (param instanceof Date) return param.toISOString();

    // Pass primitives through
    return param;
  });

  // Use a prepared statement
  const stmt = db.prepare(sql);
  stmt.bind(sqlParams);
  stmt.step();
  stmt.free();
}

/**
 * Execute multiple statements in a transaction
 */
export function transaction(callback: () => void): void {
  const db = getDB();
  try {
    db.exec('BEGIN TRANSACTION');
    callback();
    db.exec('COMMIT');
  } catch (error) {
    db.exec('ROLLBACK');
    throw error;
  }
}

// ============================================================================
// Persistence (IndexedDB)
// ============================================================================

/**
 * Save database to IndexedDB
 */
export async function saveDB(): Promise<void> {
  if (!db) return;

  try {
    const data = db.export();
    await saveToIndexedDB(data);
  } catch (error) {
    console.error('Failed to save database to IndexedDB:', error);
    throw error;
  }
}

/**
 * Load database from IndexedDB
 */
async function loadFromIndexedDB(): Promise<Uint8Array | null> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('BudgetDB', 1);

    request.onerror = () => reject(request.error);

    request.onsuccess = () => {
      const db = request.result;

      if (!db.objectStoreNames.contains('database')) {
        resolve(null);
        return;
      }

      const transaction = db.transaction(['database'], 'readonly');
      const store = transaction.objectStore('database');
      const getRequest = store.get('main');

      getRequest.onsuccess = () => {
        resolve(getRequest.result || null);
      };

      getRequest.onerror = () => reject(getRequest.error);
    };

    request.onupgradeneeded = (event: any) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('database')) {
        db.createObjectStore('database');
      }
    };
  });
}

/**
 * Save database to IndexedDB
 */
async function saveToIndexedDB(data: Uint8Array): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('BudgetDB', 1);

    request.onerror = () => reject(request.error);

    request.onsuccess = () => {
      const db = request.result;
      const transaction = db.transaction(['database'], 'readwrite');
      const store = transaction.objectStore('database');
      const putRequest = store.put(data, 'main');

      putRequest.onsuccess = () => resolve();
      putRequest.onerror = () => reject(putRequest.error);
    };

    request.onupgradeneeded = (event: any) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('database')) {
        db.createObjectStore('database');
      }
    };
  });
}

// ============================================================================
// Export/Import for Backup
// ============================================================================

/**
 * Export database as file for backup
 */
export async function exportDatabaseFile(): Promise<Blob> {
  const db = getDB();
  const data = db.export();
  return new Blob([data as BlobPart], { type: 'application/x-sqlite3' });
}

/**
 * Import database from file
 */
export async function importDatabaseFile(file: File): Promise<void> {
  const arrayBuffer = await file.arrayBuffer();
  const data = new Uint8Array(arrayBuffer);

  if (!SQL) {
    const sqlJsModule = await import('sql.js');
    const initSqlJs = sqlJsModule.default || sqlJsModule;
    SQL = await initSqlJs({
      locateFile: (_file) => `/sql-wasm.wasm`
    });
  }

  db = new SQL.Database(data);
  initialized = true;
  await saveDB();
}

/**
 * Clear all data (for testing/development)
 */
export async function clearDatabase(): Promise<void> {
  if (!db) return;

  transaction(() => {
    execute('DELETE FROM transactions');
    execute('DELETE FROM rules');
    execute('DELETE FROM processed_files');
    execute('DELETE FROM people');
    // Don't delete categories - keep them
  });

  await saveDB();
}
